/**
 * Guards against the "Failed to execute 'removeChild' / 'insertBefore' on
 * 'Node'" crash (POC-481, POCKETSFLOW-FRONTEND on /subscriptions/new and any
 * page with conditional text).
 *
 * Browser translation extensions — Google Translate / Chrome-mobile
 * auto-translate — replace React-managed text nodes with their own wrapped
 * nodes. When React's commit phase later tries to remove or move a node the
 * extension has already relocated, the raw DOM op throws `NotFoundError` and
 * React unmounts the whole tree (white screen).
 *
 * We make `removeChild` / `insertBefore` defensive: if the target isn't
 * actually a child of the expected parent (exactly the corruption case), no-op
 * instead of throwing. This keeps translation working (unlike `translate="no"`,
 * which we don't want on a globally-used app) while stopping the crash. This is
 * the widely-used React + Google-Translate workaround.
 *
 * Imported for its side effect at the very top of index.tsx, before React
 * renders.
 */
if (typeof Node === "function" && Node.prototype) {
	const originalRemoveChild = Node.prototype.removeChild;
	Node.prototype.removeChild = function <T extends Node>(child: T): T {
		if (child.parentNode !== this) {
			if (import.meta.env.DEV) {
				// eslint-disable-next-line no-console
				console.warn(
					"[domTranslationGuard] skipped removeChild on a node whose parent changed (likely a translation extension).",
				);
			}
			return child;
		}
		return originalRemoveChild.call(this, child) as T;
	};

	const originalInsertBefore = Node.prototype.insertBefore;
	Node.prototype.insertBefore = function <T extends Node>(
		newNode: T,
		referenceNode: Node | null,
	): T {
		if (referenceNode && referenceNode.parentNode !== this) {
			if (import.meta.env.DEV) {
				// eslint-disable-next-line no-console
				console.warn(
					"[domTranslationGuard] skipped insertBefore with a detached reference node (likely a translation extension).",
				);
			}
			return newNode;
		}
		return originalInsertBefore.call(this, newNode, referenceNode) as T;
	};
}
